import { Link } from 'react-router-dom';
import { homeHero, popularCities, steps } from '../homeContent';
import { locations } from '../propertyConfig';
import {
  formatPrice,
  getPropertyArea,
  getPropertyImages,
  getPropertyState,
} from '../propertyUtils';
import {
  SearchIcon,
  HomeIcon,
  RocketIcon,
  ArrowRightIcon,
  MapPinIcon,
  CheckCircleIcon,
  MessageCircleIcon,
} from './IconSet';
import heroCoupleHome from '../assets/home/photos/hero-couple-home.png';
import PropertyImageCarousel from './PropertyImageCarousel';
import AdBanner from './AdBanner';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import SeoHead from './SeoHead';
import { pageMeta } from '../seoUtils';

const HERO_LISTING_TYPES = [
  { key: 'buy', label: 'Sale' },
  { key: 'short-let', label: 'Short-let' },
  { key: 'rent', label: 'Rent' },
];

function LandingPage({
  filterLocation,
  filterArea,
  filterType,
  browseAreas,
  setFilterLocation,
  setFilterArea,
  setFilterType,
  featuredListings,
  properties,
  placeholderImage,
  onBrowse,
  onListProperty,
  onOpenProperty,
}) {
  const { user } = useAuth();
  const currencyCtx = useCurrency();
  const heroActiveType = filterType || 'rent';

  const dynamicBrowseSubLabel =
    filterArea && filterLocation
      ? `${filterArea}, ${filterLocation}`
      : filterLocation || 'All Nigeria';

  const seo = pageMeta({
    title: 'Rentals & Short-Lets Across Nigeria',
    description:
      'Find rentals and short-let stays across Nigeria, plus properties for sale and land. List your property for free on Naija Nest Properties.',
    path: '/',
  });

  const heroImageFallback = popularCities[0]?.image || placeholderImage || '';

  return (
    <>
      <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} />
      <section className="section hero-section">
        <img
          className="hero-bg-image"
          src={heroCoupleHome || heroImageFallback}
          alt=""
          aria-hidden="true"
          loading="eager"
          onError={(event) => {
            if (heroImageFallback) {
              event.currentTarget.onerror = null;
              event.currentTarget.src = heroImageFallback;
            }
          }}
        />
        <div className="hero-copy">
          <p className="hero-brand-kicker">NaijaNest • Nigeria Property Marketplace</p>
          <h1>{homeHero.title}</h1>
          <div className="hero-search-row">
            <select
              className="ui-select"
              value={filterLocation}
              onChange={(event) => {
                setFilterLocation(event.target.value);
                setFilterArea('');
              }}
            >
              <option value="">All States</option>
              {locations.map((stateName) => (
                <option key={stateName} value={stateName}>
                  {stateName}
                </option>
              ))}
            </select>
            <select
              className="ui-select"
              value={filterArea}
              onChange={(event) => setFilterArea(event.target.value)}
              disabled={!filterLocation}
            >
              <option value="">All Areas</option>
              {browseAreas.map((stateArea) => (
                <option key={stateArea} value={stateArea}>
                  {stateArea}
                </option>
              ))}
            </select>
            <div className="hero-type-toggle" role="group" aria-label="Listing type">
              {HERO_LISTING_TYPES.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className={`hero-type-toggle__btn${heroActiveType === key ? ' active' : ''}`}
                  onClick={() => setFilterType(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="ui-button hero-search-btn"
              onClick={() => onBrowse(heroActiveType)}
            >
              <SearchIcon size={16} /> Search
            </button>
          </div>
        </div>
      </section>

      <section className="content-section spotlight-section">
        <div className="section-heading">
          <h2>Featured Listings</h2>
        </div>
        <div className="featured-grid">
          {featuredListings.map((listing) => (
            <article
              key={listing.id}
              className="featured-card featured-card--clickable"
              onClick={() => onOpenProperty(listing.id)}
            >
              <PropertyImageCarousel
                images={getPropertyImages(listing, placeholderImage)}
                fallbackImage={placeholderImage}
                alt={listing.title}
                className="property-image-carousel--featured"
              />
              <div>
                <h3>
                  <button
                    className="listing-card__title-btn"
                    onClick={() => onOpenProperty(listing.id)}
                  >
                    {listing.title}
                  </button>
                </h3>
                <p>
                  {formatPrice(listing.price || 0, currencyCtx)} •{' '}
                  {getPropertyArea(listing) || 'Nigeria'}, {getPropertyState(listing) || ''}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section growth-split-section growth-split-top">
        <div className="growth-split-grid">
          <div className="how-it-works-panel">
            <div className="section-heading">
              <p className="section-kicker">
                <CheckCircleIcon size={16} /> Quick Start
              </p>
              <h2>How it works</h2>
            </div>
            <div className="how-steps-list">
              {steps.map((item, index) => (
                <article key={item.title} className="how-step-item">
                  <span className="how-step-index">{index + 1}</span>
                  <p className="how-step-copy">
                    <strong>{item.title}:</strong> {item.description}
                  </p>
                  <ArrowRightIcon size={16} />
                </article>
              ))}
            </div>
          </div>

          <aside className="seller-compact-panel">
            <p className="seller-compact-kicker">
              <HomeIcon size={14} /> For owners & agents
            </p>
            <h3>List and reach serious seekers</h3>
            <div className="seller-compact-benefits">
              <p>
                <RocketIcon size={14} /> Instant visibility
              </p>
              <p>
                <MapPinIcon size={14} /> Target area reach
              </p>
              <p>
                <MessageCircleIcon size={14} /> Direct inquiries
              </p>
            </div>
            <button
              type="button"
              className="cta-button cta-button--compact"
              onClick={onListProperty}
            >
              <RocketIcon size={16} /> Start listing now
            </button>
          </aside>
        </div>
      </section>

      <section className="content-section partner-banner-section">
        <div className="partner-banner">
          <div className="partner-banner-copy">
            <span className="partner-kicker">🤝 One Partner Program</span>
            <h2>List Properties, or Write for the Guide</h2>
            <p>
              List your properties and connect with serious tenants and buyers, or write for the
              Property Guide and reach readers. Grow your business, or your voice, with
              NaijaNest.
            </p>
          </div>
          <div className="partner-actions">
            <div className="partner-actions-buttons">
              <Link className="cta-button partner-btn" to="/become-an-agent">
                Become a Partner
              </Link>
            </div>
            <span className="partner-fineprint">First listing free for a limited time. Applying to write is free.</span>
          </div>
        </div>
      </section>

      <section className="content-section popular-locations-section">
        <div className="section-heading">
          <p className="section-kicker">
            <MapPinIcon size={16} /> Hotspots
          </p>
          <h2>Popular Locations</h2>
        </div>
        <div className="city-grid">
          {popularCities.map((city) => (
            <article key={city.name} className="city-card">
              <img src={city.image} alt={`${city.name} neighborhoods`} loading="lazy" />
              <div>
                <h3>{city.name}</h3>
                <p>{city.description}</p>
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => {
                    setFilterLocation(city.state);
                    setFilterArea('');
                    onBrowse();
                  }}
                >
                  Explore <ArrowRightIcon size={16} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section homepage-ad-section">
        <AdBanner slot="homepage" />
      </section>
    </>
  );
}

export default LandingPage;
